import { useMemo } from "react";
import { PlanTableFormat } from "../types/planning/plan.type";
import { planningStatus } from "../types/status.type";
import { ValidateMergeRequestResult } from "./useValidateMergeRequest";

interface MergeValidationError {
  message: string;
  plans: PlanTableFormat[];
}

interface UseMergeValidationResult {
  validateMerge: () => MergeValidationError[];
  hasValidationErrors: boolean;
}

export const useMergeValidation = (
  planSummaries: PlanTableFormat[],
  validateMergeRequest: () => ValidateMergeRequestResult
): UseMergeValidationResult => {
  const validateMerge = useMemo(
    () => (): MergeValidationError[] => {
      const groups: MergeValidationError[] = [];

      const warningMessage = validateMergeRequest();
      if (warningMessage) {
        return [];
      }

      const routesOrTrapsWithNoBillsError = planSummaries.filter(
        (p) => p.bills <= 0
      );
      if (routesOrTrapsWithNoBillsError.length > 0) {
        groups.push({
          message: "Cannot merge routes or traps with 0 bills.",
          plans: routesOrTrapsWithNoBillsError,
        });
      }

      const allowedStatuses: readonly string[] = [
        planningStatus.CPLN,
        planningStatus.CLDK,
        planningStatus.CLDV,
        planningStatus.TPLN,
        planningStatus.TRAP,
      ];

      const routesOrTrapsWithWrongStatusError = planSummaries.filter(
        (p) => !allowedStatuses.includes(p.status)
      );

      if (routesOrTrapsWithWrongStatusError.length > 0) {
        groups.push({
          message: "A route cannot be merged when in DISP or DEPT.",
          plans: routesOrTrapsWithWrongStatusError,
        });
      }

      return groups;
    },
    [planSummaries, validateMergeRequest]
  );

  const hasValidationErrors = useMemo(
    () => validateMerge().length > 0,
    [validateMerge]
  );

  return {
    validateMerge,
    hasValidationErrors,
  };
};
